import React, { useCallback, useRef, useState } from "react";
import { useSelector, useDispatch, RootStateOrAny } from "react-redux";
import * as S from "./styles";
import { useTranslation, useValidation } from "hooks";

import { translations } from "./translations";
import {
  DeleteUserSecondaryEmailActions,
  DeleteUserSecondaryEmailState,
  UpdateUserSecondaryEmailActions,
  UpdateUserSecondaryEmailState,
} from "store/ducks/settings/users";

import {
  differenceInCalendarDays,
  isPast,
  format,
  parseISO,
  isAfter,
  addDays,
} from "date-fns";
import { Input } from "components/shared/Form";

import { SubmitHandler, FormHandles } from "@unform/core";
import { Form } from "@unform/web";
import * as Yup from "yup";

import { Modal } from "components/shared";
interface IGridUserSecondaryEmailProps {
  emails: Record<string, any>;
  onSuccess: () => void;
}

interface ItemGroupProps {
  data: Record<string, any>;
  onSelect: (value: number) => void;
  onEdit: (data: Record<string, any>) => void;
  loadingDelete: boolean;
  loadingEdit: boolean;
}

const Item: React.FC<ItemGroupProps> = ({
  data,
  onSelect,
  onEdit,
  loadingDelete,
  loadingEdit,
}) => {
  const { getTranslation } = useTranslation(translations);

  const checkDateStatus = (targetDate: string) => {
    if (!targetDate) {
      return "---";
    }
    const today = new Date();
    const target = new Date(targetDate);

    const targetFormat = format(target, "dd/MM/yyyy");
    if (isPast(target)) {
      return `${targetFormat} - ${getTranslation("expirou")}`;
    }

    const daysRemaining = differenceInCalendarDays(target, today);
    return `${targetFormat} - ${daysRemaining} ${getTranslation(
      "diasRestantes"
    )}`;
  };

  return (
    <S.ItemContainer>
      <S.ItemContent>
        <S.ItemValue>{data.id || "--"}</S.ItemValue>
        <S.ItemValue>{checkDateStatus(data.expiration_at)}</S.ItemValue>
        <S.ItemValue>{data.email || "--"}</S.ItemValue>
        <S.ButtonTrash onClick={() => onSelect(data.id)}>
          {loadingDelete ? <S.Loading /> : <S.IconTrash />}
        </S.ButtonTrash>
        <S.ButtonEdit onClick={() => onEdit(data)}>
          {loadingEdit ? <S.Loading /> : <S.IconEdit />}
        </S.ButtonEdit>
      </S.ItemContent>
    </S.ItemContainer>
  );
};

export const GridUserEmails: React.FC<IGridUserSecondaryEmailProps> = ({
  emails = [],
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState<number | null>(null);
  const [editData, setEditData] = useState<Record<string, any> | null>(null);
  const { getTranslation } = useTranslation(translations);
  const formEmailsRef = useRef<FormHandles>(null);

  const { handleFormErrors } = useValidation();
  const { loading: loadingDelete } = useSelector<RootStateOrAny>(
    (state) => state.deleteUserSecondaryEmail
  ) as DeleteUserSecondaryEmailState;

  const { loading: loadingEdit } = useSelector<RootStateOrAny>(
    (state) => state.updateUserSecondaryEmail
  ) as UpdateUserSecondaryEmailState;

  const handleDelete = useCallback(
    (emailId: any) => {
      setSelected(emailId);
      dispatch(DeleteUserSecondaryEmailActions.request(emailId, onSuccess));
    },
    [dispatch, onSuccess]
  );

  const handleEdit = useCallback(
    (data: any) => {
      if (formEmailsRef.current) {
        formEmailsRef.current.setFieldValue(
          "expiration_at",
          format(addDays(new Date(), 1), "yyyy-MM-dd")
        );
      }

      setEditData(data);
    },
    [formEmailsRef]
  );

  const handleUpdate = useCallback<SubmitHandler>(
    async (data: any) => {
      try {
        formEmailsRef.current?.setErrors({});
        const schema = Yup.object().shape({
          expiration_at: Yup.string()
            .required("Obrigatório")
            .test(
              "is-future-date",
              "A data deve ser maior que hoje",
              (value: any) => {
                if (!value) return false;
                const date = parseISO(value);
                return isAfter(date, new Date());
              }
            ),
        });

        await schema.validate(data, { abortEarly: false });

        if (editData) {
          dispatch(
            UpdateUserSecondaryEmailActions.request(
              editData.id,
              {
                expiration_at: data.expiration_at,
                email: editData.email,
                general_user_id: editData.general_user_id,
                deleted_at: null,
                is_activy: 1,
              },
              onSuccess
            )
          );
        }

        setEditData(null);
      } catch (error) {
        handleFormErrors(error, formEmailsRef);
      }
    },
    [dispatch, editData, handleFormErrors, onSuccess]
  );

  return (
    <S.Container>
      {emails.length > 0 && (
        <>
          <S.Header>
            <S.Label>{getTranslation("codigo")}</S.Label>
            <S.Label>{getTranslation("expiração")}</S.Label>
            <S.Label>{getTranslation("email")}</S.Label>
          </S.Header>
          {emails.map((el: any) => (
            <Item
              data={el}
              key={el.id}
              onSelect={handleDelete}
              onEdit={handleEdit}
              loadingDelete={selected === el.id && loadingDelete}
              loadingEdit={editData?.id === el.id && loadingEdit}
            />
          ))}
        </>
      )}

      <Modal isOpen={editData ? true : false}>
        <S.ModalContainer>
          <p>
            Atualizando: <b>{editData?.email} </b>{" "}
          </p>
          <br />
          <Form placeholder={""} ref={formEmailsRef} onSubmit={handleUpdate}>
            <Input
              name="expiration_at"
              type="date"
              label={getTranslation("expiração")}
            />
            <S.ButtonsWrapper>
              <S.Button btStyle="cancel" onClick={() => setEditData(null)}>
                {getTranslation("cancelar")}
              </S.Button>
              <S.Button type="submit">{getTranslation("salvar")}</S.Button>
            </S.ButtonsWrapper>
          </Form>
        </S.ModalContainer>
      </Modal>
    </S.Container>
  );
};
